import type { ReactElement } from 'react';
import { Box, Button, ButtonRow } from '@ksyos/design-system';
import { useState } from 'react';
import type { IAsset } from '../../../../../../common/validation/assets';
import type { IAltoStepProps } from '../../NewAltoDermaOthProcedure';
import type { IRegularStepProps } from '../../NewDermaOthProcedure';
import { LoaderSpinner } from '../../../../elements/atoms/loaderSpinner/LoaderSpinner';
import { CustomContainer } from '../../../../elements/molecules/customContainer/CustomContainer';
import { Stack } from '../../../../elements/molecules/stack/Stack';
import { AssetThumbnail } from '../../../../elements/organisms/assetDropzone/AssetThumbnail';
import { AssetDropzoneWithThumbnails } from '../../../../elements/organisms/assetDropzoneWithThumbnails/AssetDropzoneWithThumbnails';
import { PageIntro } from '../../../../elements/organisms/pageIntro/PageIntro';
import { Instructions } from './Shared';

interface IUploadStatus {
    isUploading: boolean;
    placement: number;
}

export function Step3(props: IRegularStepProps | IAltoStepProps) {
    const [uploadStatus, setUploadStatus] = useState<IUploadStatus[]>([]);
    const isUploadInProgress = uploadStatus.some((item) => item.isUploading);
    const tokenId = 'tokenId' in props ? props.tokenId : undefined;
    const continueButtonText = isUploadInProgress ? 'Je foto wordt geladen...' : 'Verder naar de vragen';

    return (
        <>
            <PageIntro
                subtitle="Voeg drie foto’s toe van dichtbij. Kleur, vorm en dikte moeten goed te zien zijn."
                title="Detailfoto's"
            />
            <CustomContainer>
                {props.procedure.customControlData.detailFiles.map((file) => showThumbnail(file))}

                {detailFilesDropzone(0)}
                {detailFilesDropzone(1)}
                {detailFilesDropzone(2)}

                <Stack>
                    <Instructions />
                </Stack>
                <ButtonRow justify="right">
                    <Button
                        color="secondary"
                        isDisabled={isUploadInProgress || props.procedure.customControlData.detailFiles.length === 0}
                        onClick={props.validateAndGoToNextStep}
                        variant="solid"
                        width="full-below-small"
                    >
                        {continueButtonText}
                        {isUploadInProgress && <LoaderSpinner />}
                    </Button>
                </ButtonRow>
            </CustomContainer>
        </>
    );

    function detailFilesDropzone(placement: number) {
        const dropzoneFile = props.procedure.customControlData.detailFiles.find((file) => file.placement === placement);

        if (!dropzoneFile) {
            return (
                <AssetDropzoneWithThumbnails
                    acceptedFiles=".png,.jpeg,.jpg,.heic,.heif"
                    alternativeUrl={tokenId ? `/api/upload/alto/uploadUrl?tokenId=${tokenId}` : undefined}
                    files={[]}
                    handleChange={(dropzoneFiles) => {
                        dropzoneFiles.forEach((file) => (file.placement = placement));
                        props.handleChange({
                            detailFiles: [...props.procedure.customControlData.detailFiles, ...dropzoneFiles],
                        });
                    }}
                    id={placement}
                    key={placement}
                    limit={1}
                    onUploadStatusChanged={(isUploading: boolean) => {
                        const currentUploadStatus = uploadStatus.filter((status) => status.placement !== placement);
                        currentUploadStatus.push({ isUploading, placement });
                        setUploadStatus(currentUploadStatus);
                        if (props.onUploadStatusChanged) {
                            props.onUploadStatusChanged(currentUploadStatus.some((item) => item.isUploading));
                        }
                    }}
                    placeholder="Foto toevoegen"
                />
            );
        }
    }

    function showThumbnail(file: IAsset): ReactElement {
        return (
            <Box
                alignItems="center"
                display="flex"
                flexDirection="column"
            >
                <AssetThumbnail
                    file={file}
                    key={file.s3Id}
                    onThumbnailRemove={() => {
                        const detailFiles = [
                            ...props.procedure.customControlData.detailFiles.filter(
                                (asset) => asset.s3Id !== file.s3Id
                            ),
                        ];
                        props.handleChange({ detailFiles });
                    }}
                />
            </Box>
        );
    }
}
